import { DefaultRootState } from '@databases/redux';
import {
  Database,
  DatabaseFieldType,
  DatabaseValue,
  ImportedFileType,
} from '@databases/types/database';
import {
  convertFromCsv,
  getInitialValue,
  isEmptyValue,
  parseFromHtml,
} from '@databases/utils/database/field-types';
import { getTextContent, replaceBlankCharacters } from '@databases/utils/lib/dom';
import { Node } from 'domhandler';
import { parse } from 'papaparse';

import { resolveEntryReference, resolveFieldReference } from '../helpers/importHelper';
import { ImportResult, ImportSettings, ParsedPasteFile } from '../types';

/**
 * Takes an imported file generated by parseImportFile() and parses it according to the specified import settings.
 */
export function applyImportSettings(
  database: Database,
  file: ParsedPasteFile,
  settings: ImportSettings,
  state: DefaultRootState
):
  | { success: true; result: ImportResult }
  | {
      success: false;
      error: { tag: 'UNKNOWN_FILE_TYPE'; fileType: string };
    } {
  switch (file.type) {
    case ImportedFileType.HTML: {
      const cols = file.content.reduce((res, row) => Math.max(row.length, res), 0);
      const colsArr = [...new Array(cols)];
      const rows: ImportResult['rows'] = file.content.map((row, rowIdx) => {
        const entry = resolveEntryReference(database, settings.entryReferences[rowIdx]);
        return {
          isSelected: settings.selectedRows[rowIdx] ?? true,
          cols: colsArr.map((x, i) => {
            const cell = row[i];
            const field = resolveFieldReference(database, settings.fieldReferences[i]);
            let isSourceEmpty: boolean | undefined;
            let value;
            if (field) {
              if (cell) {
                value = parseFromHtml(cell, { database, field, entry: entry! }, state);
                isSourceEmpty = value.isSourceEmpty;
              } else {
                value = getInitialValue(field.type);
              }
            }

            return {
              text: cell
                ? getTextContent(cell, (node: Node) =>
                    replaceBlankCharacters(node, false)
                  )
                : '',
              value,
              isTargetEmpty:
                !value || isEmptyValue({ database, field: field!, value }, state),
              isSourceEmpty,
              targetFieldId: field?.id,
              targetEntryId: entry?.id,
              targetIsMulti: value?.value
                ? Array.isArray(value?.value) && value?.value.length > 1
                : false,
            };
          }),
        };
      });
      return { success: true, result: { colCount: cols, rows } };
    }
    case ImportedFileType.CSV: {
      const data = parse<string[]>(file.content, { skipEmptyLines: true });

      // it is possible for a user to paste a string from the clipboard. if the string is separated by new lines, it
      // will paste the content over the cells. however, if the field supports new lines, it should paste all the
      // content in one cell. only the settings of the first field is checked.
      const firstFieldReference = resolveFieldReference(
        database,
        settings.fieldReferences[0]
      );
      const allowsNewLines =
        firstFieldReference?.type === DatabaseFieldType.TEXT &&
        firstFieldReference?.settings.newlines;
      if (allowsNewLines) {
        data.data = [[data.data.join('\n')]];
      }
      const cols = data.data.reduce((res, row) => Math.max(row.length, res), 0);
      const colsArr = [...new Array(cols)];
      const rows: ImportResult['rows'] = data.data.map((row, rowIdx) => {
        const entry = resolveEntryReference(database, settings.entryReferences[rowIdx]);
        return {
          isSelected: settings.selectedRows[rowIdx] ?? true,
          cols: colsArr.map((x, i) => {
            const text = row[i] ?? '';
            const value: DatabaseValue = { type: DatabaseFieldType.TEXT, value: text };
            const field = resolveFieldReference(database, settings.fieldReferences[i]);
            const convertedValue = field
              ? convertFromCsv({ database, field, value }, field.type, state)
              : value;
            return {
              text,
              value: convertedValue,
              isTargetEmpty: field
                ? isEmptyValue({ database, field, value: convertedValue }, state)
                : !text,
              targetFieldId: field?.id,
              targetEntryId: entry?.id,
              targetIsMulti: field?.settings.multi,
              isSourceEmpty: undefined,
            };
          }),
        };
      });
      return { success: true, result: { colCount: cols, rows } };
    }

    default:
      return {
        success: false,
        error: { tag: 'UNKNOWN_FILE_TYPE', fileType: (file as any).type },
      };
  }
}
